// SocketContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { SITE_URL } from './utils/seo';

const SocketContext = createContext(null);
const SocketStateContext = createContext({ connected: false, error: null });
const UserStatusContext = createContext(new Map());

export const SocketProvider = ({ userId, children }) => {
  const [socket, setSocket] = useState(null);
  const [socketState, setSocketState] = useState({ connected: false, error: null });
  const [userStatuses, setUserStatuses] = useState(new Map());

  useEffect(() => {
    // Only attempt to connect if userId is provided
    if (!userId) {
      setSocket(null);
      setSocketState({ connected: false, error: 'No userId provided' });
      return;
    }

    // Reset cached statuses when the active account changes (impersonation switch)
    setUserStatuses(new Map());

    console.log('🔌 [SocketContext] Initializing socket connection for userId:', userId);
    
    const newSocket = io(SITE_URL, {
      withCredentials: true,
      transports: ['websocket'], // выбор для производительности!
    });

    newSocket.on('connect', () => {
      console.log(`✅ [SocketContext] Connected with ID: ${newSocket.id}`);
      console.log(`🔄 [SocketContext] Joining room for userId: ${userId}`);
      newSocket.emit('join', userId);
      setSocketState({ connected: true, error: null });
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ [SocketContext] Connection error:', error.message);
      setSocketState({ connected: false, error: error.message });
    });

    newSocket.on('disconnect', (reason) => {
      console.log(`⚠️ [SocketContext] Disconnected. Reason: ${reason}`);
      setSocketState({ connected: false, error: reason });
    });

    // Listen for user status changes
    newSocket.on('user_status_changed', (data) => {
      console.log(`👤 [SocketContext] User status changed:`, data);
      setUserStatuses(prev => {
        const next = new Map(prev);
        if (data.isOnline) {
          next.set(data.userId, true);
        } else {
          next.delete(data.userId);
        }
        return next;
      });
    });

    setSocket(newSocket);

    return () => {
      console.log('🔌 [SocketContext] Cleanup: disconnecting socket');
      newSocket.disconnect();
    };
  }, [userId]);

  return (
    <SocketStateContext.Provider value={socketState}>
      <UserStatusContext.Provider value={userStatuses}>
        <SocketContext.Provider value={socket}>
          {children}
        </SocketContext.Provider>
      </UserStatusContext.Provider>
    </SocketStateContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
export const useSocketState = () => useContext(SocketStateContext);
export const useUserStatuses = () => useContext(UserStatusContext);

export default SocketContext;
